import { useState, useCallback } from "react";
import {
  ArrowLeft,
  ClipboardCheck,
  Loader2,
  AlertCircle,
  ShieldCheck,
  Play,
} from "lucide-react";
import { apiv2 } from "../apiv2";
import type { ValidationDryRunResponse, ValidationStrictness } from "../apiv2";
import GreenTendrils from "./GreenTendrils";

interface Props {
  onBack: () => void;
  projectId: string;
}

const SEVERITY_STYLE: Record<string, string> = {
  critical: "bg-red-500/15 text-red-400 border-red-500/30",
  high: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  medium: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  low: "bg-white/5 text-white/40 border-white/10",
};

export default function DataValidationView({ onBack }: Props) {
  const [rulePack, setRulePack] = useState("dq-rules.core");
  const [rulePackVersion, setRulePackVersion] = useState("3.0.0");
  const [strictness, setStrictness] =
    useState<ValidationStrictness>("standard");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ValidationDryRunResponse | null>(null);

  const runDryRun = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiv2.validation.dryRun({
        rule_pack: rulePack,
        rule_pack_version: rulePackVersion,
        strictness,
      });
      setResult(response);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to preview the rule pack.",
      );
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [rulePack, rulePackVersion, strictness]);

  return (
    <div className="relative min-h-screen">
      <GreenTendrils />

      <div className="relative z-10 max-w-4xl mx-auto p-6 flex flex-col h-screen">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4 flex-shrink-0">
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-surface-200 text-white/40 hover:text-white transition"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-teal-500/15">
            <ClipboardCheck size={20} className="text-teal-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Data Validator</h1>
            <p className="text-xs text-white/30">
              Preview which data quality rules a rule pack would apply
            </p>
          </div>
        </div>

        <div className="mb-4 p-3 bg-white/5 border border-white/10 rounded-2xl text-xs text-white/40 flex items-start gap-2 flex-shrink-0">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          This preview shows what a rule pack would evaluate. Running validation
          against a live dataset and producing a violation report isn't
          available yet — that stage still lands in a later release.
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4 py-2">
          {/* Config form */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="text-xs text-white/40 space-y-1">
              Rule pack
              <input
                value={rulePack}
                onChange={(e) => setRulePack(e.target.value)}
                className="w-full bg-surface-200 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-teal-500/50 transition"
              />
            </label>
            <label className="text-xs text-white/40 space-y-1">
              Version
              <input
                value={rulePackVersion}
                onChange={(e) => setRulePackVersion(e.target.value)}
                className="w-full bg-surface-200 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-teal-500/50 transition"
              />
            </label>
            <label className="text-xs text-white/40 space-y-1">
              Strictness
              <select
                value={strictness}
                onChange={(e) =>
                  setStrictness(e.target.value as ValidationStrictness)
                }
                className="w-full bg-surface-200 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-teal-500/50 transition"
              >
                <option value="loose">Loose</option>
                <option value="standard">Standard</option>
                <option value="strict">Strict</option>
              </select>
            </label>
          </div>

          <button
            onClick={runDryRun}
            disabled={loading || !rulePack || !rulePackVersion}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-500 text-white text-sm font-medium transition disabled:opacity-40 disabled:hover:bg-teal-600"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Play size={16} />
            )}
            Preview rule pack
          </button>

          {error && (
            <div className="p-2.5 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-300 flex items-center gap-2">
              <AlertCircle size={13} /> {error}
            </div>
          )}

          {result && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs text-white/40">
                <ShieldCheck size={14} className="text-teal-400" />
                {result.rule_count} rule
                {result.rule_count === 1 ? "" : "s"} selected from{" "}
                {result.rule_pack}@{result.rule_pack_version} at{" "}
                {result.strictness} strictness (severities:{" "}
                {result.applied_severities.join(", ")})
              </div>

              <div className="space-y-2">
                {result.rules.map((rule) => (
                  <div
                    key={rule.rule_id}
                    className="bg-surface-200 border border-white/5 rounded-xl p-3 flex items-start gap-3"
                  >
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-medium border flex-shrink-0 ${
                        SEVERITY_STYLE[rule.severity] ?? SEVERITY_STYLE.low
                      }`}
                    >
                      {rule.severity}
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm text-white/80 flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[11px] text-white/40">
                          {rule.rule_id}
                        </span>
                        <span className="text-[11px] text-white/30">
                          {rule.category}
                        </span>
                      </div>
                      <p className="text-xs text-white/50 mt-1">
                        {rule.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
